import React from "react";
import { useTranslation } from "react-i18next";
import { Mic, MicOff } from "lucide-react";
import { Button } from "@core/components/ui/button";
import { cn } from "@core/lib/utils";
import type {
  VoiceRecognitionState,
  VoiceRecognitionActions,
} from "@core/hooks/useVoiceRecognition";

interface VoiceInputButtonProps {
  voiceRecognition: VoiceRecognitionState & VoiceRecognitionActions;
  className?: string;
  size?: "default" | "sm" | "lg" | "icon";
}

export function VoiceInputButton({
  voiceRecognition,
  className,
  size = "default",
}: VoiceInputButtonProps) {
  const { t } = useTranslation();
  const {
    isSupported,
    isListening,
    startListening,
    stopListening,
    resetTranscript,
    error,
    isUnsupportedBrowser,
  } = voiceRecognition;

  if (!isSupported) return null;

  return (
    <>
      <Button
        type="button"
        onClick={() => {
          if (isListening) {
            stopListening();
          } else {
            resetTranscript();
            startListening();
          }
        }}
        className={cn(
          "transition-colors",
          isListening
            ? "bg-red-100 text-red-600 animate-pulse hover:bg-red-200"
            : "bg-gray-100 text-gray-600 hover:bg-amber-100 hover:text-amber-600",
          className,
        )}
        size={size}
        title={isListening ? t("common:voice_stop") : t("common:voice_start")}
        aria-label={
          isListening ? t("common:voice_stop") : t("common:voice_start")
        }
        data-testid="voice-input-button"
      >
        {isListening ? (
          <MicOff className="w-5 h-5" />
        ) : (
          <Mic className="w-5 h-5" />
        )}
      </Button>

      {/* Message d'erreur dictée vocale */}
      {error && (
        <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
          <MicOff className="w-3 h-3" />
          {error}
        </p>
      )}

      {/* Message si navigateur non supporté (Safari iOS) */}
      {isUnsupportedBrowser && (
        <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
          <MicOff className="w-3 h-3" />
          {t("common:voice_unsupported_browser")}
        </p>
      )}
    </>
  );
}
